import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { type ReferentPriceType } from '@strata/constants';
import { ActionButtonText } from '@strata/react-buttons';
import { FormInputContainer } from '@strata/react-form-input-container';
import { BidInput } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { Icon } from '@strata/react-status-icons';
import { validationStrings } from '@strata/strata-ui-strings';
import Decimal from 'decimal.js';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import { BidTermsAndConditionsCheckbox } from 'components/shared/BidTermsAndConditionsCheckbox';
import {
    ALL_PAYERS,
    type AutomationRule,
    AutomationRuleActions,
    AutomationRuleCriteria,
    type AutomationRuleFormContext,
    findAutomationRuleCriterionValue,
} from 'services/AutomationRuleHelpers';
import { type DefaultRate, useGetDefaultRatesForProductQuery } from 'store/api/rateSetterApi';

/** Finds the highest default rate for the minimum allowed bid. */
const getMinAllowedBid = (
    defaultRates: DefaultRate[] | undefined,
    referentPriceType: ReferentPriceType,
) => {
    // The highest default rate for a price type is the first matching rate since the list is in descending order.
    const highestDefaultRate = defaultRates?.find(
        (rate) => rate.referentPriceType === referentPriceType,
    )?.defaultRate;

    return highestDefaultRate != null ? Decimal.mul(highestDefaultRate, 100).toNumber() : undefined;
};

interface ActionSectionProps {
    actionOptions: (typeof AutomationRuleActions)[keyof typeof AutomationRuleActions][];
    userCanEdit: boolean;
    isExistingRule: boolean;
}

export const ActionSection = ({
    actionOptions,
    userCanEdit,
    isExistingRule,
}: ActionSectionProps) => {
    const formik = useFormikContext<AutomationRuleFormContext>();

    // Get the referent price types
    const selectedSegment = findAutomationRuleCriterionValue(
        formik.values.criteria,
        AutomationRuleCriteria.PAYER,
    );
    const payerId = selectedSegment?.id !== ALL_PAYERS ? selectedSegment?.payerId : undefined;
    const productServiceOid = findAutomationRuleCriterionValue(
        formik.values.criteria,
        AutomationRuleCriteria.PRODUCT,
    )?.oid;
    const placeOfService = findAutomationRuleCriterionValue(
        formik.values.criteria,
        AutomationRuleCriteria.PLACE_OF_SERVICE,
    )?.id;
    const isBrand = findAutomationRuleCriterionValue(
        formik.values.criteria,
        AutomationRuleCriteria.PRODUCT,
    )?.isBrand;

    const { data: defaultRates, isFetching: isDefaultRatesFetching } =
        useGetDefaultRatesForProductQuery(
            productServiceOid && isBrand != null
                ? {
                      payerId,
                      productServiceOid,
                      placeOfService,
                      isBrand,
                  }
                : skipToken,
        );
    const referentPriceTypes = [...new Set(defaultRates?.map((rate) => rate.referentPriceType))];

    const getActionComponent = () => {
        if (formik.values.action?.type === AutomationRuleActions.SET_MAX_BID) {
            return (
                <>
                    <div className="sds_automation-template__label">to</div>
                    <FieldArray name="action.value">
                        {(arrayHelpers) => {
                            const actionValue = getIn(
                                formik.values,
                                arrayHelpers.name,
                            ) as AutomationRuleFormContext['action']['value'];
                            return (
                                <div>
                                    {actionValue?.map(
                                        ({ referentPriceType, tempId }, index, value) => (
                                            <div
                                                key={referentPriceType ?? tempId}
                                                className="flex__gap--small margin__bottom--medium"
                                            >
                                                <div style={{ flexGrow: 1 }}>
                                                    {referentPriceType ? (
                                                        <BidInput
                                                            id={`${arrayHelpers.name}.${index}.bidAmt`}
                                                            referentPriceType={referentPriceType}
                                                            disabled={!userCanEdit}
                                                            // Don't allow positive bids if highest default rate is negative
                                                            minAllowedBid={getMinAllowedBid(
                                                                defaultRates,
                                                                referentPriceType,
                                                            )}
                                                        />
                                                    ) : (
                                                        <SuggestSingleSelectFormik
                                                            id={`${arrayHelpers.name}.${index}.referentPriceType`}
                                                            disabled={!userCanEdit}
                                                            required
                                                            placeholderText="Select Referent Price Type"
                                                            isLoading={isDefaultRatesFetching}
                                                            // Filter out already selected referent price types
                                                            values={referentPriceTypes.filter(
                                                                (type) =>
                                                                    !value?.find(
                                                                        (val) =>
                                                                            val.referentPriceType ===
                                                                            type,
                                                                    ),
                                                            )}
                                                        />
                                                    )}
                                                </div>
                                                <Icon
                                                    className="margin__top--small"
                                                    icon={IconNames.CROSS}
                                                    title="Remove"
                                                    onClick={() => arrayHelpers.remove(index)}
                                                />
                                            </div>
                                        ),
                                    )}
                                    {((actionValue ?? []).length ?? 0) <
                                        referentPriceTypes.length && (
                                        <ActionButtonText
                                            className="margin__bottom--small"
                                            leftIcon={IconNames.ADD}
                                            label="Add Referent Price Type"
                                            id="add-referent-price-type-button"
                                            disabled={!userCanEdit}
                                            // Store temp id for array keys
                                            onClick={() => arrayHelpers.push({ tempId: uuidv4() })}
                                        />
                                    )}
                                    <BidTermsAndConditionsCheckbox
                                        namePrefix="action"
                                        disabled={!userCanEdit || isExistingRule}
                                    />
                                </div>
                            );
                        }}
                    </FieldArray>
                </>
            );
        }
        return null;
    };

    return (
        <FormInputContainer>
            <div className="sds_automation-template__row">
                <div className="sds_automation-template__label sds_label font-bold--uppercase">
                    Then
                </div>
                <div style={{ gridColumn: 2 }}>
                    <SuggestSingleSelectFormik
                        id="action.type"
                        placeholderText="Select Action"
                        disabled={!userCanEdit || isExistingRule}
                        values={actionOptions}
                        required
                        disableAutoPopulate={!formik.dirty}
                        onChange={(newValue) => {
                            void formik.setFieldValue('action', {
                                type: newValue,
                                value: [{ tempId: uuidv4() }],
                            });
                        }}
                    />
                </div>
                {getActionComponent()}
            </div>
        </FormInputContainer>
    );
};

ActionSection.initialValues = (
    rule: Partial<AutomationRule> | undefined,
    isExistingRule: boolean,
) => ({
    action: {
        type: rule?.action?.type,
        value: rule?.action?.value,
        ...(rule?.action?.type === AutomationRuleActions.SET_MAX_BID
            ? BidTermsAndConditionsCheckbox.initialValues(isExistingRule)
            : undefined),
    },
});

ActionSection.validationSchema = {
    action: Yup.object({
        type: Yup.string().required(validationStrings.requiredField.errorMessage),
        value: Yup.array().when('type', {
            is: AutomationRuleActions.SET_MAX_BID,
            then: (schema) =>
                schema
                    .of(
                        Yup.object({
                            bidAmt: BidInput.validator().required(
                                validationStrings.requiredField.errorMessage,
                            ),
                        }),
                    )
                    .required(validationStrings.requiredField.errorMessage)
                    .min(1, validationStrings.requiredField.errorMessage),
        }),
        terms: Yup.array().when('type', {
            is: AutomationRuleActions.SET_MAX_BID,
            then: () => BidTermsAndConditionsCheckbox.validationSchema.terms,
        }),
    }).required(validationStrings.requiredField.errorMessage),
};

ActionSection.submitValues = (values: AutomationRuleFormContext) => {
    if (values.action?.type === AutomationRuleActions.SET_MAX_BID) {
        return {
            bidAmts: values.action?.value?.map(({ bidAmt, referentPriceType }) => ({
                bidAmt: Decimal.div(bidAmt, 100).toNumber(),
                referentPriceType,
            })),
        };
    }
    return {};
};

ActionSection.duplicateValues = (values: AutomationRuleFormContext) => ({
    action: {
        type: values.action?.type,
        value: [{ tempId: uuidv4() }],
    },
});
